import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Spacer,
  Button,
  IconButton,
  Tooltip,
  Badge,
  Skeleton,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { AiOutlineArrowLeft, AiOutlineDelete, AiOutlinePlus } from "react-icons/ai";

import countriesService from "../../services/countries.service";
import AuthService from "../../services/auth.service";
import ConfirmDialog from "./confirmDialog";

// Lists countries (read-only — no add/edit) with file usage, and allows deleting
// a country only when nothing references it (and it isn't the default).
function CountryManagement(props) {
  const toast = useToast();
  const canManage = AuthService.hasPermission("manageRules");
  const confirm = useDisclosure();
  const addModal = useDisclosure();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");

  const notify = (title, status) => toast({ title, position: "top", status, isClosable: true });

  const load = async () => {
    setLoading(true);
    if (props.setisLoading) props.setisLoading(true);
    const res = await countriesService.getCountries();
    setLoading(false);
    if (props.setisLoading) props.setisLoading(false);
    if (res && res.countries) setCountries(res.countries);
    else notify((res && (res.warning || res.error)) || "Failed to load countries", "error");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reasonNotDeletable = (c) =>
    c.isDefault ? "Default country — cannot be deleted" : `${c.counts.total} file(s) associated`;

  const onSetDefault = async (c) => {
    if (props.setisLoading) props.setisLoading(true);
    const res = await countriesService.setDefault(c.code);
    if (props.setisLoading) props.setisLoading(false);
    if (res && res.defaultCountry) {
      notify(`${c.name} (${c.code}) is now the default country`, "success");
      load();
    } else {
      notify((res && (res.warning || res.error)) || "Could not set default", "error");
    }
  };

  const onAddCountry = async () => {
    const name = newName.trim();
    const code = newCode.trim().toUpperCase();
    if (!name || !code) {
      notify("Enter a country name and code", "warning");
      return;
    }
    if (props.setisLoading) props.setisLoading(true);
    const res = await countriesService.createCountry({ name, code });
    if (props.setisLoading) props.setisLoading(false);
    if (res && res.code) {
      notify(`Added ${res.name} (${res.code})`, "success");
      setNewName("");
      setNewCode("");
      addModal.onClose();
      load();
    } else {
      notify((res && (res.warning || res.error)) || "Add failed", "error");
    }
  };

  const onDeleteClick = (c) => {
    setPending(c);
    confirm.onOpen();
  };

  const performDelete = async () => {
    if (!pending) return;
    if (props.setisLoading) props.setisLoading(true);
    const res = await countriesService.deleteCountry(pending.code);
    if (props.setisLoading) props.setisLoading(false);
    if (res && res.code) {
      notify(`Deleted ${pending.name} (${pending.code})`, "success");
      load();
    } else {
      notify((res && (res.warning || res.error)) || "Delete failed", "error");
    }
    setPending(null);
  };

  return (
    <Box w="100%">
      <Flex alignItems="center" mb={3}>
        <Button size="xs" leftIcon={<AiOutlineArrowLeft />} variant="ghost" onClick={props.onBack}>
          Back to settings
        </Button>
        <Spacer />
        <Heading size="sm">Country Management</Heading>
        <Spacer />
        {canManage && (
          <Button size="xs" leftIcon={<AiOutlinePlus />} colorScheme="teal" variant="outline" onClick={() => addModal.onOpen()}>
            Add country
          </Button>
        )}
      </Flex>

      {loading ? (
        <Stack spacing={2} mt={1}>
          <Skeleton height="28px" borderRadius="md" />
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height="34px" borderRadius="md" />
          ))}
        </Stack>
      ) : (
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Country</Th>
              <Th>Code</Th>
              <Th isNumeric>Cases</Th>
              <Th isNumeric>Uploads</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {countries.map((c) => (
              <Tr key={c.code}>
                <Td>
                  {c.name}{" "}
                  {c.isDefault && (
                    <Badge ml={1} colorScheme="purple" fontSize="0.6em">
                      default
                    </Badge>
                  )}
                </Td>
                <Td>{c.code}</Td>
                <Td isNumeric>{c.counts.cases}</Td>
                <Td isNumeric>{c.counts.history}</Td>
                <Td textAlign="right">
                  {canManage && !c.isDefault && (
                    <Button size="xs" variant="ghost" colorScheme="teal" mr={1} onClick={() => onSetDefault(c)}>
                      Set as default
                    </Button>
                  )}
                  {canManage && (
                    <Tooltip
                      label={c.deletable ? "Delete country" : reasonNotDeletable(c)}
                      shouldWrapChildren
                      hasArrow
                    >
                      <IconButton
                        aria-label="Delete country"
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        icon={<AiOutlineDelete />}
                        isDisabled={!c.deletable}
                        onClick={() => onDeleteClick(c)}
                      />
                    </Tooltip>
                  )}
                </Td>
              </Tr>
            ))}
            {countries.length === 0 && (
              <Tr>
                <Td colSpan={5}>
                  <Text fontSize="sm" color="gray.500">
                    No countries.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      )}

      <ConfirmDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        onReset={performDelete}
        header="Delete country"
        body={pending ? `Delete ${pending.name} (${pending.code})? This cannot be undone.` : ""}
      />

      <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Add a country</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Name</FormLabel>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Malaysia" autoFocus />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Code</FormLabel>
              <Input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="e.g. MY"
                maxLength={5}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onAddCountry();
                }}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                2–5 letters, unique (e.g. MY, US).
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" mr={2} variant="ghost" onClick={addModal.onClose}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="teal" onClick={onAddCountry}>
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default CountryManagement;
